import React, { useState, useEffect, useRef, useCallback } from 'react';
import VoiceFeedback, { VoiceFeedbackConfig, VoiceFeedbackResult } from './index';

export interface VoiceFeedbackHookOptions extends Omit<VoiceFeedbackConfig, 'onStart' | 'onStop' | 'onComplete' | 'onError'> {
  onStart?: () => void;
  onStop?: () => void;
  onComplete?: (result: VoiceFeedbackResult) => void;
  onError?: (error: Error) => void;
}

export interface VoiceFeedbackHookReturn {
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  isRecording: boolean;
  duration: number;
  isSupported: boolean;
  error: Error | null;
}

export function useVoiceFeedback(options: VoiceFeedbackHookOptions): VoiceFeedbackHookReturn {
  const [isRecording, setIsRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<Error | null>(null);
  const voiceFeedbackRef = useRef<VoiceFeedback | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize VoiceFeedback instance
  useEffect(() => {
    const config: VoiceFeedbackConfig = {
      ...options,
      onStart: () => {
        setIsRecording(true);
        setError(null);
        options.onStart?.();
        
        // Start duration timer
        intervalRef.current = setInterval(() => {
          if (voiceFeedbackRef.current) {
            const status = voiceFeedbackRef.current.getStatus();
            setDuration(status.duration);
          }
        }, 100);
      },
      onStop: () => {
        setIsRecording(false);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        options.onStop?.();
      },
      onComplete: (result) => {
        setDuration(0);
        options.onComplete?.(result);
      },
      onError: (err) => {
        setError(err);
        setIsRecording(false);
        setDuration(0);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        options.onError?.(err);
      }
    };

    voiceFeedbackRef.current = new VoiceFeedback(config);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [options]);

  const startRecording = useCallback(async () => {
    if (voiceFeedbackRef.current) {
      try {
        await voiceFeedbackRef.current.startRecording();
      } catch (err) {
        setError(err as Error);
      }
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (voiceFeedbackRef.current) {
      voiceFeedbackRef.current.stopRecording();
    }
  }, []);

  return {
    startRecording,
    stopRecording,
    isRecording,
    duration,
    isSupported: VoiceFeedback.isSupported(),
    error
  };
}

export interface VoiceFeedbackButtonProps extends VoiceFeedbackHookOptions {
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  variant?: 'primary' | 'secondary' | 'minimal' | 'glass';
  shape?: 'circle' | 'rounded' | 'square';
  showDuration?: boolean;
  showWaveform?: boolean;
  recordingText?: string;
  idleText?: string;
}

// Modern microphone icon component
function MicrophoneIcon({ size = 24, isRecording = false }: { size?: number; isRecording?: boolean }) {
  return (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 24 24" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      style={{
        transition: 'all 0.3s ease',
        transform: isRecording ? 'scale(1.1)' : 'scale(1)',
      }}
    >
      <path
        d="M12 2C10.9 2 10 2.9 10 4V12C10 13.1 10.9 14 12 14C13.1 14 14 13.1 14 12V4C14 2.9 13.1 2 12 2Z"
        fill="currentColor"
      />
      <path
        d="M19 10V12C19 15.87 15.87 19 12 19C8.13 19 5 15.87 5 12V10H7V12C7 14.76 9.24 17 12 17C14.76 17 17 14.76 17 12V10H19Z"
        fill="currentColor"
      />
      <path
        d="M12 19V22H5V20H19V22H12Z"
        fill="currentColor"
      />
    </svg>
  );
}

// Modern waveform animation component
function WaveformAnimation({ isActive }: { isActive: boolean }) {
  if (!isActive) return null;

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '2px',
      marginLeft: '8px'
    }}>
      {[1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          style={{
            width: '2px',
            backgroundColor: 'currentColor',
            borderRadius: '1px',
            animation: `voicefeedback-wave 1.2s ease-in-out ${i * 0.1}s infinite`,
            height: '12px'
          }}
        />
      ))}
      <style>{`
        @keyframes voicefeedback-wave {
          0%, 100% { height: 4px; opacity: 0.4; }
          50% { height: 16px; opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// Get theme-based styles
function getButtonStyles(variant: string, shape: string, size: string, isRecording: boolean) {
  const sizes = {
    small: { padding: '8px 16px', fontSize: '14px', iconSize: 16, minWidth: '40px', height: '40px' },
    medium: { padding: '12px 24px', fontSize: '16px', iconSize: 20, minWidth: '48px', height: '48px' },
    large: { padding: '16px 32px', fontSize: '18px', iconSize: 24, minWidth: '56px', height: '56px' }
  };

  const shapeStyles = {
    circle: { borderRadius: '50%' },
    rounded: { borderRadius: '12px' },
    square: { borderRadius: '4px' }
  };

  const variantStyles = {
    primary: {
      background: isRecording 
        ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      border: 'none',
      boxShadow: isRecording 
        ? '0 4px 20px rgba(239, 68, 68, 0.4), 0 0 0 0 rgba(239, 68, 68, 0.7)'
        : '0 4px 20px rgba(102, 126, 234, 0.4)',
    },
    secondary: {
      background: isRecording
        ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
        : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
      color: isRecording ? 'white' : '#374151',
      border: '1px solid #d1d5db',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    },
    minimal: {
      background: isRecording ? 'rgba(239, 68, 68, 0.1)' : 'rgba(102, 126, 234, 0.1)',
      color: isRecording ? '#ef4444' : '#667eea',
      border: `2px solid ${isRecording ? '#ef4444' : '#667eea'}`,
      boxShadow: 'none',
    },
    glass: {
      background: isRecording 
        ? 'rgba(239, 68, 68, 0.15)'
        : 'rgba(255, 255, 255, 0.15)',
      color: isRecording ? '#ef4444' : '#374151',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      backdropFilter: 'blur(10px)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    }
  };

  return {
    ...sizes[size as keyof typeof sizes],
    ...shapeStyles[shape as keyof typeof shapeStyles],
    ...variantStyles[variant as keyof typeof variantStyles],
  };
}

export function VoiceFeedbackButton({
  children,
  className = '',
  style,
  disabled = false,
  size = 'medium',
  variant = 'primary',
  shape = 'rounded',
  showDuration = true,
  showWaveform = true,
  recordingText,
  idleText,
  ...hookOptions
}: VoiceFeedbackButtonProps): JSX.Element {
  const { startRecording, stopRecording, isRecording, duration, isSupported, error } = useVoiceFeedback(hookOptions);

  const handleClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const buttonStyles = getButtonStyles(variant, shape, size, isRecording);
  const isCircle = shape === 'circle';

  if (!isSupported) {
    return (
      <button 
        disabled 
        className={`voicefeedback-button voicefeedback-unsupported ${className}`}
        style={{
          ...buttonStyles,
          opacity: 0.6,
          cursor: 'not-allowed',
          background: '#9ca3af',
          color: 'white',
          ...style
        }}
      >
        <MicrophoneIcon size={buttonStyles.iconSize} />
        {!isCircle && <span style={{ marginLeft: '8px' }}>Not Supported</span>}
      </button>
    );
  }

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
      <button
        onClick={handleClick}
        disabled={disabled}
        className={`voicefeedback-button ${isRecording ? 'voicefeedback-recording' : 'voicefeedback-idle'} ${className}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          fontWeight: '500',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          outline: 'none',
          position: 'relative',
          overflow: 'hidden',
          ...buttonStyles,
          ...style,
          animation: isRecording ? 'voicefeedback-pulse 1.5s ease-in-out infinite' : 'none',
        }}
        onMouseEnter={(e) => {
          if (!disabled && !isRecording) {
            e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
            e.currentTarget.style.boxShadow = buttonStyles.boxShadow + ', 0 8px 25px rgba(0, 0, 0, 0.15)';
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled) {
            e.currentTarget.style.transform = 'translateY(0) scale(1)';
            e.currentTarget.style.boxShadow = buttonStyles.boxShadow;
          }
        }}
      >
        <MicrophoneIcon size={buttonStyles.iconSize} isRecording={isRecording} />
        
        {!isCircle && (
          <>
            {showWaveform && <WaveformAnimation isActive={isRecording} />}
            {children || (
              <span style={{ marginLeft: showWaveform && isRecording ? '4px' : '8px' }}>
                {isRecording 
                  ? (recordingText || 'Recording...') 
                  : (idleText || 'Voice Feedback')
                }
              </span>
            )}
          </>
        )}

        {/* Ripple effect */}
        <style>{`
          @keyframes voicefeedback-pulse {
            0% { box-shadow: ${buttonStyles.boxShadow}, 0 0 0 0 rgba(239, 68, 68, 0.7); }
            70% { box-shadow: ${buttonStyles.boxShadow}, 0 0 0 10px rgba(239, 68, 68, 0); }
            100% { box-shadow: ${buttonStyles.boxShadow}, 0 0 0 0 rgba(239, 68, 68, 0); }
          }
        `}</style>
      </button>

      {/* Duration display */}
      {showDuration && isRecording && (
        <div style={{
          fontSize: '14px',
          fontWeight: '500',
          color: variant === 'glass' ? '#374151' : '#6b7280',
          fontFamily: 'monospace',
          background: 'rgba(255, 255, 255, 0.9)',
          padding: '4px 8px',
          borderRadius: '12px',
          border: '1px solid rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(10px)',
          animation: 'voicefeedback-fadeIn 0.3s ease-out'
        }}>
          {formatDuration(duration)}
        </div>
      )}

      {/* Error display */}
      {error && (
        <div style={{ 
          fontSize: '12px', 
          color: '#ef4444',
          textAlign: 'center',
          maxWidth: '200px',
          background: 'rgba(254, 226, 226, 0.9)',
          padding: '6px 12px',
          borderRadius: '8px',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          animation: 'voicefeedback-slideIn 0.3s ease-out'
        }}>
          {error.message}
        </div>
      )}

      <style>{`
        @keyframes voicefeedback-fadeIn {
          from { opacity: 0; transform: translateY(5px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes voicefeedback-slideIn {
          from { opacity: 0; transform: translateY(10px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}

// Export hook and component for easy imports
export default VoiceFeedbackButton; 